import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listWaiverGrantsBySection, updateWaiverGrant, deleteWaiverGrant } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const categoryApi = createLookupApi("student-categories");
const feeWaiverApi = createLookupApi("fee-waivers");

/**
 * Direct port of digicampusbd's real `fee-waiver-configuration-list` ("Fee
 * Waiver List") — a genuinely separate real screen from "Fee Waiver Setup"
 * (`FeeWaiverGrantPage`): every already-granted waiver for one whole
 * section+category+year, each row editable (waiver type + amount) or
 * removable. Confirmed via the old app's own real
 * `FeeWaiverConfigurationService.feeConfigurationViewList`/
 * `updateFeeWaiverConfig`. This migration previously had no roster-wide view
 * at all (only a single-student one) — see MIGRATION.md.
 */
export default function FeeWaiverListPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [feeWaivers, setFeeWaivers] = useState([]);

  const [rows, setRows] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    categoryApi.list().then((cats) => {
      setCategories(cats);
      if (cats.length > 0) setCategoryId(String(cats[0].id));
    });
    feeWaiverApi.list().then(setFeeWaivers);
  }, []);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!academicYear || !classConfigId || !categoryId) {
      setError("Please select a year, section, and category.");
      return;
    }
    setSearching(true);
    try {
      const data = await listWaiverGrantsBySection(classConfigId, categoryId, academicYear);
      setRows(data);
    } catch (err) {
      setError("Failed to load waiver grants.");
    } finally {
      setSearching(false);
    }
  };

  const updateRow = (id, field, value) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const handleSaveRow = async (row) => {
    setError("");
    try {
      await updateWaiverGrant(row.id, { feeWaiverId: Number(row.feeWaiverId), waiverAmount: Number(row.waiverAmount) });
      setSuccess("Waiver updated.");
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove this waiver for "${row.studentName}"?`)) return;
    try {
      await deleteWaiverGrant(row.id);
      setSuccess("Removed.");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Waiver List</h1>
      </div>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Student Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </div>

      {rows && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Roll</th>
                <th>Student</th>
                <th>Fee Head</th>
                <th>Sub-Head</th>
                <th>Waiver Type</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.studentRoll}</td>
                  <td>{r.studentName}</td>
                  <td>{r.feeHeadName || "Any"}</td>
                  <td>{r.feeSubHeadName || "Any"}</td>
                  <td>
                    <select value={r.feeWaiverId} onChange={(e) => updateRow(r.id, "feeWaiverId", e.target.value)}>
                      {feeWaivers.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      style={{ width: 90 }}
                      value={r.waiverAmount}
                      onChange={(e) => updateRow(r.id, "waiverAmount", e.target.value)}
                    />
                  </td>
                  <td className="actions-col">
                    <button type="button" className="btn-link" onClick={() => handleSaveRow(r)}>
                      Save
                    </button>
                    <button type="button" className="btn-link danger" onClick={() => handleDelete(r)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7}>No waivers granted for this selection.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </MainLayout>
  );
}
